import React, { useState, useEffect } from 'react';
import theme from '../theme.config';

/**
 * HeroVisual — site-specific decorative visual that replaces the legacy mascot.
 * Variant is driven by `theme.style.heroVisual` (string token). Each variant
 * is pure CSS/SVG, no external image assets needed.
 *
 * Tokens supported:
 *   - 'controller-orb'   (default — concentric pulsing rings)
 *   - 'circuit-grid'     (cyberpunk grid + scanline)
 *   - 'pixel-tiles'      (8-bit blocky tiles)
 *   - 'gold-orbs'        (premium glass orbs)
 *   - 'crosshair-radar'  (tactical radar sweep)
 *   - 'deep-abyss'       (deep-sea jellyfish + bioluminescent particles)
 *   - 'terminal-feed'    (matrix code rain on black + terminal log)
 *   - 'dune-caravan'     (desert nomad — dunes + camel silhouette + setting sun)
 *   - 'molecule-orbit'   (clean lab — atom with electron orbitals)
 *   - 'rune-circle'      (viking forge — engraved rune disc)
 *   - 'enso-ink'         (Japanese zen — enso brush circle + falling petal)
 *   - 'sun-horizon'      (synthwave — retro sun grid horizon)
 *   - 'candle-vigil'     (gothic — candle flame + wax drips + cobweb)
 */
const VARIANT = (theme.style && theme.style.heroVisual) || 'controller-orb';

function ControllerOrb() {
  return (
    <div className="relative w-80 h-80">
      <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl animate-pulse" />
      <div className="absolute inset-6 rounded-full border-2 border-primary/40 animate-spin-slow" />
      <div className="absolute inset-14 rounded-full border-2 border-accent/40" />
      <div className="absolute inset-24 rounded-full bg-gradient-to-br from-primary to-accent shadow-2xl" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full border-4 border-white/10 backdrop-blur-sm bg-gradient-to-tr from-primary/30 to-accent/30" />
      </div>
    </div>
  );
}

function CircuitGrid() {
  return (
    <div className="relative w-80 h-80 overflow-hidden rounded-2xl border border-primary/30 bg-black/40">
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'linear-gradient(hsl(var(--primary) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.4) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-secondary/30 via-transparent to-accent/40" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-accent/60 blur-2xl animate-pulse" />
      <div className="absolute left-0 right-0 h-1 bg-primary/70 animate-scan" />
      <div className="absolute right-4 bottom-4 font-mono text-xs text-primary/80 tracking-widest">
        // {theme.brand.short.toUpperCase()}_GRID.SYS
      </div>
    </div>
  );
}

function PixelTiles() {
  const tiles = Array.from({ length: 36 });
  const palette = ['bg-primary', 'bg-secondary', 'bg-accent', 'bg-success', 'bg-background'];
  return (
    <div className="relative w-80 h-80 grid grid-cols-6 grid-rows-6 gap-1 p-3 bg-black/40 rounded-xl border-4 border-primary/60 shadow-[8px_8px_0_0_hsl(var(--secondary))]">
      {tiles.map((_, i) => (
        <div
          key={i}
          className={`${palette[(i * 7) % palette.length]} animate-pixel-pulse`}
          style={{ animationDelay: `${(i % 12) * 0.08}s` }}
        />
      ))}
    </div>
  );
}

function GoldOrbs() {
  return (
    <div className="relative w-80 h-80">
      <div className="absolute left-10 top-10 w-40 h-40 rounded-full bg-gradient-to-br from-primary to-accent opacity-80 blur-sm shadow-2xl" />
      <div className="absolute right-6 bottom-12 w-28 h-28 rounded-full bg-gradient-to-br from-accent/70 to-primary/40 backdrop-blur-md border border-primary/30" />
      <div className="absolute right-16 top-6 w-20 h-20 rounded-full border-2 border-primary/60" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="font-serif italic text-7xl text-primary/40 select-none">
          {theme.brand.short[0]}
        </div>
      </div>
    </div>
  );
}

function CrosshairRadar() {
  return (
    <div className="relative w-80 h-80 rounded-full border-2 border-primary/60 bg-radial-radar overflow-hidden">
      {[0.85, 0.65, 0.45, 0.25].map((scale, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/40"
          style={{ width: `${scale * 100}%`, height: `${scale * 100}%` }}
        />
      ))}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-primary/40 -translate-x-1/2" />
      <div className="absolute top-1/2 left-0 right-0 h-px bg-primary/40 -translate-y-1/2" />
      <div
        className="absolute left-1/2 top-1/2 w-1/2 h-px origin-left animate-radar-sweep"
        style={{ background: 'linear-gradient(to right, hsl(var(--accent)), transparent)' }}
      />
      <div className="absolute left-[60%] top-[35%] w-2 h-2 rounded-full bg-secondary animate-pulse" />
      <div className="absolute left-[30%] top-[60%] w-2 h-2 rounded-full bg-accent animate-pulse" />
      <div className="absolute right-4 top-4 font-mono text-xs text-primary/80 tracking-widest">
        [ {theme.brand.short.toUpperCase()}-RADAR ]
      </div>
    </div>
  );
}

function DeepAbyss() {
  const particles = Array.from({ length: 18 });
  const tentacles = [30, 50, 70, 90, 110];
  const smallTentacles = [14, 26, 38, 50];
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-gradient-to-b from-[#001018] via-[#000812] to-black border border-primary/30">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
      <div className="absolute -top-10 left-10 w-2 h-72 bg-gradient-to-b from-primary/40 to-transparent rotate-6 blur-sm" />
      <div className="absolute -top-10 right-20 w-1 h-64 bg-gradient-to-b from-primary/30 to-transparent -rotate-3 blur-sm" />
      <div className="absolute -top-10 left-40 w-1.5 h-60 bg-gradient-to-b from-secondary/30 to-transparent rotate-12 blur" />
      {particles.map((_, i) => {
        const left = (i * 53) % 100;
        const top = (i * 37 + 7) % 100;
        const size = 2 + (i % 3);
        return (
          <span
            key={i}
            className="absolute rounded-full bg-primary animate-pulse"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: `${size}px`,
              height: `${size}px`,
              boxShadow: '0 0 8px currentColor, 0 0 14px currentColor',
              animationDelay: `${(i * 0.4) % 5}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          />
        );
      })}
      <div className="absolute left-1/2 top-[28%] -translate-x-1/2 animate-float-slow">
        <svg width="140" height="160" viewBox="0 0 140 160">
          <defs>
            <radialGradient id="jelly1" cx="50%" cy="35%" r="60%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
              <stop offset="60%" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
              <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0" />
            </radialGradient>
          </defs>
          <path d="M20 60 Q70 -10 120 60 L120 70 Q70 60 20 70 Z" fill="url(#jelly1)" />
          <path d="M30 50 Q70 0 110 50" fill="none" stroke="white" strokeOpacity="0.35" strokeWidth="1" />
          <path d="M40 40 Q70 8 100 40" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="1" />
          {tentacles.map((x, i) => (
            <path
              key={i}
              d={`M${x} 70 Q${x - 4} 100 ${x + 3} 130 Q${x - 3} 150 ${x + 1} 160`}
              fill="none"
              stroke="hsl(var(--accent))"
              strokeOpacity="0.55"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          ))}
          <circle cx="70" cy="50" r="6" fill="white" opacity="0.7" />
        </svg>
      </div>
      <div className="absolute right-4 top-10 animate-float-slow" style={{ animationDelay: '1.2s' }}>
        <svg width="70" height="90" viewBox="0 0 70 90">
          <path d="M8 30 Q35 0 62 30 L62 35 Q35 30 8 35 Z" fill="hsl(var(--accent))" fillOpacity="0.5" />
          {smallTentacles.map((x, i) => (
            <path
              key={i}
              d={`M${x} 35 Q${x - 2} 55 ${x + 1} 80`}
              fill="none"
              stroke="hsl(var(--accent))"
              strokeOpacity="0.6"
              strokeWidth="1"
            />
          ))}
        </svg>
      </div>
      <div className="absolute left-3 bottom-3 font-mono text-[10px] tracking-[0.3em] text-primary/70">
        -2400m · ABYSS
      </div>
    </div>
  );
}

function TerminalFeed() {
  const lines = [
    '> initialize quantum.node',
    '  loading kernel...........[OK]',
    '  handshake digiflazz......[OK]',
    '  link ayolinx.tx..........[OK]',
    '> deploy --order=QTM-7341',
    '  signing.................[OK]',
    '  routing.................[OK]',
    '> drop sequence engaged_',
  ];
  const cols = Array.from({ length: 14 });
  const promptLabel = `${theme.brand.short.toLowerCase()}@core ~ %`;
  return (
    <div className="relative w-80 h-80 rounded-xl overflow-hidden bg-black border border-primary/60 shadow-[0_0_40px_rgba(16,185,129,0.25)]">
      <div className="absolute inset-0 opacity-60">
        {cols.map((_, i) => {
          const ch1 = String.fromCharCode(0x30a0 + (i * 13) % 96);
          const ch2 = String.fromCharCode(0x30a0 + (i * 7) % 96);
          const ch3 = String.fromCharCode(0x30a0 + (i * 23) % 96);
          const ch4 = String.fromCharCode(0x30a0 + (i * 17) % 96);
          const num = (i * 1297) % 9999;
          return (
            <span
              key={i}
              className="absolute top-0 font-mono text-[10px] text-primary animate-rain"
              style={{
                left: `${(i * 7.5) % 100}%`,
                animationDelay: `${(i * 0.4) % 3}s`,
                animationDuration: `${4 + (i % 4)}s`,
                writingMode: 'vertical-rl',
                textShadow: '0 0 6px currentColor',
              }}
            >
              {ch1}{ch2}{ch3} 01101 {ch4} {num}
            </span>
          );
        })}
      </div>
      <div className="absolute left-3 right-3 bottom-3 bg-black/85 border border-primary/50 rounded-md p-3 backdrop-blur-sm">
        <div className="flex items-center gap-1 mb-2">
          <span className="w-2 h-2 rounded-full bg-destructive/80" />
          <span className="w-2 h-2 rounded-full bg-accent/80" />
          <span className="w-2 h-2 rounded-full bg-primary/80" />
          <span className="ml-2 font-mono text-[10px] text-primary/70 tracking-widest">
            {promptLabel}
          </span>
        </div>
        {lines.map((ln, i) => (
          <div
            key={i}
            className="font-mono text-[10px] leading-relaxed text-primary"
            style={{ opacity: 0.55 + (i / lines.length) * 0.45 }}
          >
            {ln}
          </div>
        ))}
        <span className="inline-block w-2 h-3 bg-primary animate-pulse align-middle ml-1" />
      </div>
    </div>
  );
}

function DuneCaravan() {
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-gradient-to-b from-[#FCD9A4] via-[#E89766] to-[#7C2D12]">
      <div
        className="absolute top-10 right-12 w-28 h-28 rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(var(--accent)), hsl(var(--primary)) 70%, transparent 78%)',
          boxShadow: '0 0 80px hsl(var(--accent) / 0.7)',
        }}
      />
      <div className="absolute inset-0">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="absolute left-0 right-0 rounded-[50%]"
            style={{
              bottom: `${i * 18}%`,
              height: `${22 + i * 4}%`,
              background: `hsl(var(--secondary) / ${0.85 - i * 0.18})`,
              transform: `translateY(50%) scaleX(${1 + i * 0.15})`,
            }}
          />
        ))}
      </div>
      <svg className="absolute left-1/4 bottom-[34%]" width="120" height="60" viewBox="0 0 120 60" fill="hsl(var(--foreground))" fillOpacity="0.85">
        <path d="M10 50 L15 30 Q18 20 22 28 L25 22 Q28 16 31 24 L40 50 Z" />
        <circle cx="20" cy="38" r="2" />
        <path d="M50 50 L55 30 Q58 20 62 28 L65 20 Q68 14 71 22 L80 50 Z" />
        <circle cx="60" cy="38" r="2" />
        <path d="M88 50 L92 36 L96 32 L100 36 L104 50 Z" />
      </svg>
      <div className="absolute top-6 left-6 flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-foreground/40" />
        ))}
      </div>
      <div className="absolute left-4 bottom-3 font-serif italic text-xs text-foreground/80 tracking-widest">
        ~ ROUTE 1462 · {theme.brand.short.toUpperCase()} ~
      </div>
    </div>
  );
}

function MoleculeOrbit() {
  return (
    <div className="relative w-80 h-80 flex items-center justify-center bg-card/30 rounded-3xl border border-border">
      <div
        className="absolute"
        style={{
          width: '85%',
          height: '40%',
          border: '2px solid hsl(var(--primary) / 0.45)',
          borderRadius: '50%',
        }}
      />
      <div
        className="absolute animate-spin-slow"
        style={{
          width: '85%',
          height: '40%',
          border: '2px solid hsl(var(--accent) / 0.55)',
          borderRadius: '50%',
          transform: 'rotate(60deg)',
        }}
      />
      <div
        className="absolute"
        style={{
          width: '85%',
          height: '40%',
          border: '2px solid hsl(var(--secondary) / 0.45)',
          borderRadius: '50%',
          transform: 'rotate(-60deg)',
        }}
      />
      <div
        className="absolute top-1/2 left-[10%] w-3 h-3 rounded-full bg-primary -translate-y-1/2 animate-orbit"
        style={{ boxShadow: '0 0 12px currentColor' }}
      />
      <div
        className="absolute top-1/2 right-[10%] w-2.5 h-2.5 rounded-full bg-accent -translate-y-1/2 animate-orbit"
        style={{ animationDelay: '1.2s', boxShadow: '0 0 10px currentColor' }}
      />
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary via-accent to-secondary shadow-[0_0_40px_hsl(var(--primary)/0.6)] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-background/40 backdrop-blur-sm border border-white/40" />
      </div>
      <div className="absolute right-4 top-4 font-mono text-[10px] text-foreground/60 tracking-[0.3em]">
        H₂{theme.brand.short[0].toUpperCase()} · LAB
      </div>
    </div>
  );
}

function RuneCircle() {
  const runes = ['ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ', 'ᚲ', 'ᚷ', 'ᚹ', 'ᚺ', 'ᚾ', 'ᛁ', 'ᛃ'];
  return (
    <div className="relative w-80 h-80 flex items-center justify-center">
      <div className="absolute inset-0 rounded-full bg-gradient-radial from-accent/40 via-primary/10 to-transparent blur-2xl" />
      <div className="relative w-72 h-72 rounded-full border-4 border-accent/70 bg-card shadow-[0_0_60px_hsl(var(--accent)/0.5),inset_0_0_40px_hsl(var(--primary)/0.3)]">
        <div className="absolute inset-3 rounded-full border border-accent/40" />
        <div className="absolute inset-8 rounded-full border border-accent/30" />
        {runes.map((r, i) => {
          const angle = (i / runes.length) * 360;
          return (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 font-serif text-2xl text-accent"
              style={{
                transform: `rotate(${angle}deg) translateY(-130px) rotate(-${angle}deg) translate(-50%, -50%)`,
                textShadow: '0 0 8px currentColor',
              }}
            >
              {r}
            </div>
          );
        })}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-accent/80 shadow-[0_0_30px_hsl(var(--accent))] flex items-center justify-center animate-pulse">
            <span className="font-serif text-4xl text-background font-bold">
              {theme.brand.short[0]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function EnsoInk() {
  return (
    <div className="relative w-80 h-80 bg-card rounded-3xl overflow-hidden border border-border">
      <div className="absolute top-6 right-6 w-10 h-10">
        <div className="w-full h-full bg-accent" style={{ clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)' }} />
      </div>
      <svg className="absolute inset-0 m-auto" width="280" height="280" viewBox="0 0 280 280">
        <defs>
          <filter id="brush">
            <feTurbulence baseFrequency="0.85" numOctaves="3" stitchTiles="stitch" />
            <feDisplacementMap in="SourceGraphic" scale="3" />
          </filter>
        </defs>
        <path
          d="M 80 140 A 60 60 0 1 1 200 140 A 60 60 0 0 1 140 200"
          fill="none"
          stroke="hsl(var(--foreground))"
          strokeWidth="14"
          strokeLinecap="round"
          filter="url(#brush)"
          opacity="0.92"
        />
      </svg>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute w-3 h-4 rounded-full bg-accent/80 animate-float-slow"
          style={{
            left: `${20 + i * 30}%`,
            top: `${15 + i * 18}%`,
            transform: 'rotate(35deg)',
            animationDelay: `${i * 1.2}s`,
          }}
        />
      ))}
      <div className="absolute left-6 bottom-6 font-serif text-xs text-foreground/70" style={{ writingMode: 'vertical-rl' }}>
        無 ・ {theme.brand.short.toLowerCase()}
      </div>
    </div>
  );
}

function SunHorizon() {
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-gradient-to-b from-[#1E1B4B] via-[#7C3AED] to-[#0F172A]">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 w-56 h-56 rounded-full overflow-hidden">
        <div className="w-full h-full bg-gradient-to-b from-accent via-primary to-secondary" />
        {[20, 35, 48, 60, 72, 84].map((y, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 bg-background"
            style={{ top: `${y}%`, height: `${2 + i * 0.5}px` }}
          />
        ))}
      </div>
      <div
        className="absolute left-0 right-0 bottom-0 h-1/2"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%), repeating-linear-gradient(0deg, transparent 0, transparent 14px, hsl(var(--primary) / 0.5) 14px, hsl(var(--primary) / 0.5) 15px), repeating-linear-gradient(90deg, transparent 0, transparent 28px, hsl(var(--primary) / 0.5) 28px, hsl(var(--primary) / 0.5) 29px)',
          perspective: '200px',
          transform: 'rotateX(60deg)',
          transformOrigin: 'top',
        }}
      />
      {[10, 25, 70, 88].map((left, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-foreground animate-pulse"
          style={{ left: `${left}%`, top: `${5 + i * 4}%`, width: '2px', height: '2px' }}
        />
      ))}
      <div className="absolute right-4 top-4 font-mono text-xs text-accent/90 tracking-[0.3em]">
        ◢◤ {theme.brand.short.toUpperCase()}.84
      </div>
    </div>
  );
}

function CandleVigil() {
  return (
    <div className="relative w-80 h-80 rounded-3xl overflow-hidden bg-black border border-primary/30">
      <svg className="absolute left-0 top-0" width="120" height="120" viewBox="0 0 120 120" fill="none" stroke="hsl(var(--secondary) / 0.5)" strokeWidth="0.5">
        <path d="M0 0 L120 60 M0 0 L60 120 M0 0 L120 120 M0 0 L120 30 M0 0 L30 120" />
      </svg>
      <svg className="absolute right-0 bottom-0 rotate-180" width="120" height="120" viewBox="0 0 120 120" fill="none" stroke="hsl(var(--secondary) / 0.5)" strokeWidth="0.5">
        <path d="M0 0 L120 60 M0 0 L60 120 M0 0 L120 120 M0 0 L120 30 M0 0 L30 120" />
      </svg>
      <div className="absolute left-1/2 top-12 -translate-x-1/2 w-40 h-40 rounded-full bg-accent/30 blur-3xl animate-pulse" />
      <div className="absolute left-1/2 top-20 -translate-x-1/2">
        <div className="relative w-6 h-12">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 w-3 h-12 bg-accent rounded-t-full animate-pulse" style={{ boxShadow: '0 0 16px hsl(var(--accent)), 0 0 30px hsl(var(--primary))' }} />
          <div className="absolute left-1/2 top-3 -translate-x-1/2 w-1.5 h-6 bg-foreground rounded-full" />
        </div>
      </div>
      <div className="absolute left-1/2 top-32 -translate-x-1/2 w-16 h-44 bg-gradient-to-b from-secondary via-foreground to-secondary/40 rounded-sm">
        <div className="absolute -bottom-2 left-0 right-0 h-4 bg-secondary blur-sm" />
        <div className="absolute top-0 left-1 right-1 h-2 bg-black/40" />
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute bg-foreground/80 rounded-full"
            style={{
              left: `${15 + i * 30}%`,
              top: `${20 + i * 15}%`,
              width: '4px',
              height: `${20 + i * 6}px`,
              borderBottomLeftRadius: '50%',
              borderBottomRightRadius: '50%',
            }}
          />
        ))}
      </div>
      <div className="absolute left-4 bottom-4 font-serif italic text-xs text-primary/90 tracking-widest">
        † {theme.brand.short.toUpperCase()} · MMXXVI †
      </div>
    </div>
  );
}

function AstraFlux() {
  const particles = Array.from({ length: 18 });
  return (
    <div className="relative w-64 h-64 rounded-full border border-primary/20 bg-gradient-to-b from-[#0a0520] to-[#090314] overflow-hidden"
      style={{ boxShadow: '0 0 40px rgba(0,255,136,0.12), 0 0 80px rgba(255,0,170,0.06)' }}>

      {/* Background dot grid */}
      <div className="absolute inset-0 opacity-25" style={{
        backgroundImage: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 1px, transparent 1px)',
        backgroundSize: '14px 14px',
      }} />

      {/* Scanline texture */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,136,0.08) 2px, rgba(0,255,136,0.08) 3px)',
      }} />

      {/* Space particles */}
      {particles.map((_, i) => {
        const left = (i * 53 + 7) % 100;
        const top = (i * 31 + 17) % 100;
        const size = 1 + (i % 2.5);
        const delay = (i * 0.35) % 4;
        const dur = 2.5 + (i % 3);
        const isGreen = i % 3 !== 2;
        return (
          <span key={i} className="absolute rounded-full animate-pulse"
            style={{
              left: `${left}%`, top: `${top}%`,
              width: `${size}px`, height: `${size}px`,
              background: isGreen ? 'hsl(var(--primary))' : 'hsl(var(--secondary))',
              boxShadow: isGreen
                ? '0 0 4px hsl(var(--primary)), 0 0 8px hsl(var(--primary))'
                : '0 0 4px hsl(var(--secondary)), 0 0 8px hsl(var(--secondary))',
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
            }}
          />
        );
      })}

      {/* Orbit 1 — neon green dashed */}
      <div className="absolute inset-6 rounded-full animate-spin-slow" style={{
        border: '1px dashed hsl(var(--primary) / 0.5)',
        transform: 'rotate(25deg)',
        boxShadow: '0 0 8px hsl(var(--primary) / 0.2)',
        animationDuration: '18s',
      }} />

      {/* Orbit 2 — magenta solid */}
      <div className="absolute inset-14 rounded-full animate-spin" style={{
        border: '1.5px solid hsl(var(--secondary) / 0.6)',
        transform: 'rotate(-45deg)',
        boxShadow: '0 0 10px hsl(var(--secondary) / 0.3)',
        animationDuration: '7s',
      }} />

      {/* Orbit 3 — accent violet faint */}
      <div className="absolute inset-[72px] rounded-full animate-spin-slow" style={{
        border: '1px solid hsl(var(--accent) / 0.3)',
        animationDuration: '30s',
        animationDirection: 'reverse',
      }} />

      {/* SVG ellipse orbits */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 256 256">
        <ellipse cx="128" cy="128" rx="110" ry="32" fill="none"
          stroke="url(#afGrad1)" strokeWidth="1.2" strokeDasharray="4,8"
          transform="rotate(-20 128 128)"
          style={{ animation: 'spinSlow 50s linear infinite' }} />
        <ellipse cx="128" cy="128" rx="80" ry="22" fill="none"
          stroke="url(#afGrad2)" strokeWidth="1.2"
          transform="rotate(40 128 128)"
          style={{ animation: 'spinSlow 35s linear infinite reverse' }} />
        <defs>
          <linearGradient id="afGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
            <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="afGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity="0.8" />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.2" />
          </linearGradient>
        </defs>
      </svg>

      {/* Pulsar core */}
      <div className="absolute inset-0 flex items-center justify-center animate-float" style={{ animationDuration: '5s' }}>
        <div className="relative w-20 h-20 flex items-center justify-center">
          {/* Outer glow layers */}
          <div className="absolute w-20 h-20 rounded-full blur-xl animate-pulse"
            style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.3), transparent)', animationDuration: '2s' }} />
          <div className="absolute w-14 h-14 rounded-full blur-md"
            style={{ background: 'radial-gradient(circle, hsl(var(--secondary) / 0.35), transparent)' }} />
          <div className="absolute w-9 h-9 rounded-full blur-sm animate-ping"
            style={{ background: 'hsl(var(--primary) / 0.2)', animationDuration: '2.5s' }} />
          {/* Core sphere */}
          <div className="w-8 h-8 rounded-full flex items-center justify-center z-10"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))',
              boxShadow: '0 0 20px hsl(var(--primary) / 0.8), 0 0 40px hsl(var(--primary) / 0.4)',
            }}>
            <div className="w-2.5 h-2.5 rounded-full bg-white" style={{ boxShadow: '0 0 8px #fff' }} />
          </div>
          {/* Cross flares */}
          <div className="absolute w-1 h-12 rounded-full animate-pulse"
            style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--primary) / 0.6), transparent)', animationDuration: '2s' }} />
          <div className="absolute h-1 w-12 rounded-full animate-pulse"
            style={{ background: 'linear-gradient(to right, transparent, hsl(var(--secondary) / 0.5), transparent)', animationDuration: '2.5s' }} />
        </div>
      </div>

      {/* HUD labels */}
      <div className="absolute bottom-3 left-0 right-0 text-center font-mono text-[8px] tracking-[0.35em] animate-flicker"
        style={{ color: 'hsl(var(--primary) / 0.7)' }}>
        [ FLUX_CORE_ACTIVE ]
      </div>
      <div className="absolute top-3 left-4 font-mono text-[7px] tracking-widest"
        style={{ color: 'hsl(var(--muted-foreground))' }}>
        SEC:ORBIT_Σ
      </div>
      <div className="absolute top-3 right-4 font-mono text-[7px]"
        style={{ color: 'hsl(var(--secondary) / 0.6)' }}>
        ● SYNC
      </div>
    </div>
  );
}


function NeuroVanta() {
  return (
    <div className="relative w-80 h-80 flex items-center justify-center">
      {/* Bioluminescent forest/ocean ambient glow */}
      <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl animate-pulse-slow" />
      <div className="absolute inset-10 rounded-full bg-secondary/10 blur-2xl animate-pulse" style={{ animationDuration: '4s' }} />

      {/* SVG Container */}
      <svg className="w-full h-full relative z-10" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="globeGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00FF9D" />
            <stop offset="100%" stopColor="#00D4FF" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer orbital rings (Latitude/Longitude) */}
        <g className="animate-spin-slow" style={{ transformOrigin: '100px 100px', animationDuration: '25s' }}>
          <ellipse cx="100" cy="100" rx="85" ry="30" stroke="url(#globeGrad)" strokeWidth="0.8" fill="none" opacity="0.3" transform="rotate(-15 100 100)" />
          <ellipse cx="100" cy="100" rx="85" ry="30" stroke="url(#globeGrad)" strokeWidth="0.8" fill="none" opacity="0.3" transform="rotate(75 100 100)" strokeDasharray="5 5" />
          <ellipse cx="100" cy="100" rx="75" ry="40" stroke="url(#globeGrad)" strokeWidth="0.6" fill="none" opacity="0.4" transform="rotate(135 100 100)" />
        </g>
        
        {/* Counter rotating rings */}
        <g className="animate-spin-slow" style={{ transformOrigin: '100px 100px', animationDuration: '16s', animationDirection: 'reverse' }}>
          <ellipse cx="100" cy="100" rx="60" ry="60" stroke="#00D4FF" strokeWidth="0.5" fill="none" opacity="0.25" strokeDasharray="3 7" />
          <circle cx="100" cy="100" r="45" stroke="#00FF9D" strokeWidth="0.75" fill="none" opacity="0.25" strokeDasharray="8 4" />
        </g>

        {/* Global connection routes (Root curves) */}
        <path d="M 55,70 Q 100,20 145,70" stroke="#00FF9D" strokeWidth="1" fill="none" strokeDasharray="4 4" className="animate-pulse" opacity="0.6" />
        <path d="M 55,130 Q 100,180 145,130" stroke="#00D4FF" strokeWidth="1" fill="none" strokeDasharray="5 3" className="animate-pulse" opacity="0.6" />
        <path d="M 30,100 Q 100,100 170,100" stroke="url(#globeGrad)" strokeWidth="0.75" fill="none" strokeDasharray="10 5" opacity="0.5" />
        
        {/* Vertical root lines */}
        <path d="M 100,30 Q 70,100 100,170" stroke="#00FF9D" strokeWidth="0.8" fill="none" opacity="0.4" />
        <path d="M 100,30 Q 130,100 100,170" stroke="#00D4FF" strokeWidth="0.8" fill="none" opacity="0.4" />

        {/* Core Seed/Nucleus */}
        <g transform="translate(100, 100)">
          {/* Pulsing energy particles */}
          <circle cx="0" cy="0" r="28" fill="none" stroke="#FFB300" strokeWidth="0.5" opacity="0.15" className="animate-ping" style={{ animationDuration: '3s' }} />
          <circle cx="0" cy="0" r="20" fill="url(#globeGrad)" opacity="0.2" filter="url(#glow)" />
          <circle cx="0" cy="0" r="12" fill="url(#globeGrad)" opacity="0.65" />
          <circle cx="0" cy="0" r="6" fill="#FFF" filter="url(#glow)" />
        </g>

        {/* Synaptic Node Terminals (Floating city connections) */}
        {/* Node 1 */}
        <g transform="translate(55, 70)" className="animate-pixel-pulse">
          <circle cx="0" cy="0" r="3.5" fill="#00FF9D" filter="url(#glow)" />
          <circle cx="0" cy="0" r="1.5" fill="#FFF" />
        </g>
        {/* Node 2 */}
        <g transform="translate(145, 70)" className="animate-pixel-pulse" style={{ animationDelay: '0.4s' }}>
          <circle cx="0" cy="0" r="3.5" fill="#00D4FF" filter="url(#glow)" />
          <circle cx="0" cy="0" r="1.5" fill="#FFF" />
        </g>
        {/* Node 3 */}
        <g transform="translate(55, 130)" className="animate-pixel-pulse" style={{ animationDelay: '0.8s' }}>
          <circle cx="0" cy="0" r="3.5" fill="#00D4FF" filter="url(#glow)" />
          <circle cx="0" cy="0" r="1.5" fill="#FFF" />
        </g>
        {/* Node 4 */}
        <g transform="translate(145, 130)" className="animate-pixel-pulse" style={{ animationDelay: '1.2s' }}>
          <circle cx="0" cy="0" r="3.5" fill="#00FF9D" filter="url(#glow)" />
          <circle cx="0" cy="0" r="1.5" fill="#FFF" />
        </g>
        {/* Node 5 (Top Core) */}
        <g transform="translate(100, 30)" className="animate-pixel-pulse" style={{ animationDelay: '0.2s' }}>
          <circle cx="0" cy="0" r="4.5" fill="#FFB300" filter="url(#glow)" />
          <circle cx="0" cy="0" r="2" fill="#FFF" />
        </g>
        {/* Node 6 (Bottom Core) */}
        <g transform="translate(100, 170)" className="animate-pixel-pulse" style={{ animationDelay: '1.0s' }}>
          <circle cx="0" cy="0" r="4" fill="#00FF9D" filter="url(#glow)" />
          <circle cx="0" cy="0" r="1.5" fill="#FFF" />
        </g>

        {/* Photosynthesis Energy Signals (Solar Gold Dust) */}
        <circle cx="45" cy="50" r="1" fill="#FFB300" className="animate-bounce" style={{ animationDuration: '4s' }} />
        <circle cx="155" cy="150" r="1" fill="#FFB300" className="animate-bounce" style={{ animationDuration: '5s', animationDelay: '1s' }} />
        <circle cx="70" cy="150" r="1.5" fill="#FFB300" className="animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }} />
        <circle cx="130" cy="40" r="1.2" fill="#FFB300" className="animate-bounce" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }} />
      </svg>

      {/* Decorative text readout */}
      <div className="absolute bottom-2 left-0 right-0 text-center font-mono text-[7px] tracking-[0.4em] text-primary/75 animate-flicker">
        [ GAIA_BIOSPHERE_ONLINE ]
      </div>
      <div className="absolute top-2 left-2 font-mono text-[6px] text-muted-foreground/75 uppercase tracking-wider">
        Node: Earth.Core-v3
      </div>
      <div className="absolute top-2 right-2 font-mono text-[6px] text-accent/75 uppercase tracking-wider">
        ● SYNAPSE_LINKED
      </div>
    </div>
  );
}

function ApiConsole() {
  const [activeTab, setActiveTab] = useState('POST');
  const [copied, setCopied] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const curlSnippet = `curl -X POST "https://api.neurovanta.io/v1/transmissions" \\
  -H "Authorization: Bearer nv_live_7a39..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "product": "mlbb_500",
    "target": "81920843",
    "region": "asia"
  }'`;

  const responseSnippet = `{
  "status": "success",
  "transmission_id": "tx_9a8f21bc7d",
  "routed_via": "ground_station_02",
  "latency_ms": 8.4,
  "payload": {
    "product_name": "500 Diamonds",
    "recipient_id": "81920843",
    "settled_at": "2026-06-10T01:45:00Z"
  }
}`;

  return (
    <div className="w-full max-w-sm bg-[#09090b]/80 border border-zinc-800 rounded-xl overflow-hidden shadow-2xl font-mono text-[10px] sm:text-xs text-left">
      {/* Console Header */}
      <div className="bg-[#121214] px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
        </div>
        <span className="text-[10px] text-zinc-500 font-semibold tracking-wider">api_playground.sh</span>
        <button 
          onClick={() => {
            navigator.clipboard.writeText(curlSnippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="text-[10px] text-zinc-400 hover:text-white transition-colors bg-zinc-800/50 hover:bg-zinc-800 px-2 py-0.5 rounded border border-zinc-700/50"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-[#121214]/50 px-4 py-1.5 border-b border-zinc-800 flex gap-4 text-[10px]">
        <button 
          className={`pb-1 ${activeTab === 'POST' ? 'text-primary border-b border-primary' : 'text-zinc-500'}`}
          onClick={() => setActiveTab('POST')}
        >
          REQUEST (cURL)
        </button>
        <button 
          className={`pb-1 ${activeTab === 'RESPONSE' ? 'text-primary border-b border-primary' : 'text-zinc-500'}`}
          onClick={() => setActiveTab('RESPONSE')}
        >
          RESPONSE (JSON)
        </button>
      </div>

      {/* Console Content */}
      <div className="p-4 bg-black/40 min-h-[170px] overflow-x-auto text-[11px] leading-relaxed">
        {activeTab === 'POST' ? (
          <pre className="text-zinc-300">
            <span className="text-emerald-500">$</span> {curlSnippet}
          </pre>
        ) : (
          <pre className="text-cyan-400">
            {responseSnippet}
          </pre>
        )}
      </div>

      {/* Footer / Telemetry bar */}
      <div className="bg-[#121214] px-4 py-2 border-t border-zinc-800 flex items-center justify-between text-[10px] text-zinc-500">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span>Latency: <strong className="text-zinc-300">8.4ms</strong></span>
        </div>
        <span className="text-zinc-600">v1.2.4</span>
      </div>
    </div>
  );
}

function EarthSynapse() {
  return (
    <div className="relative w-80 h-80 sm:w-96 sm:h-96 mx-auto flex items-center justify-center">
      {/* Outer spinning dash borders */}
      <div className="absolute inset-0 rounded-[2.5rem] border border-dashed border-emerald-500/20 animate-spin-slow pointer-events-none" />
      <div className="absolute inset-4 rounded-[2rem] border border-cyan-500/10 animate-reverse-spin pointer-events-none" />
      <div className="absolute inset-8 rounded-[1.75rem] border border-zinc-850 pointer-events-none" />

      {/* Glow aura */}
      <div className="absolute inset-12 rounded-[1.75rem] bg-emerald-500/10 blur-3xl animate-pulse pointer-events-none" />

      {/* Main Image Container */}
      <div className="absolute inset-10 rounded-[1.5rem] overflow-hidden border border-zinc-800 shadow-2xl bg-zinc-950 flex items-center justify-center">
        <img
          src="/hero-neurovanta.png"
          alt="Earth Synapse network"
          className="w-full h-full object-cover"
        />
        {/* Subtle overlay gradients */}
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/10 via-transparent to-cyan-500/10 mix-blend-color-add pointer-events-none" />
        
        {/* Scanning beam */}
        <div className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent top-0 animate-scanline opacity-60 pointer-events-none" />
      </div>

      {/* Floating Status Badges */}
      <div className="absolute top-4 left-4 bg-zinc-950/90 backdrop-blur border border-zinc-800 rounded-lg px-2.5 py-1 flex items-center gap-1.5 shadow-lg">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <span className="font-mono text-[9px] text-zinc-300 uppercase tracking-wider">Sync: Online</span>
      </div>
      <div className="absolute bottom-4 right-4 bg-zinc-950/90 backdrop-blur border border-zinc-800 rounded-lg px-2.5 py-1 flex items-center gap-1.5 shadow-lg">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        <span className="font-mono text-[9px] text-zinc-300 uppercase tracking-wider">Channels: Active</span>
      </div>
    </div>
  );
}

const VARIANTS = {
  'controller-orb': ControllerOrb,
  'circuit-grid': CircuitGrid,
  'pixel-tiles': PixelTiles,
  'gold-orbs': GoldOrbs,
  'crosshair-radar': CrosshairRadar,
  'deep-abyss': DeepAbyss,
  'terminal-feed': TerminalFeed,
  'dune-caravan': DuneCaravan,
  'molecule-orbit': MoleculeOrbit,
  'rune-circle': RuneCircle,
  'enso-ink': EnsoInk,
  'sun-horizon': SunHorizon,
  'candle-vigil': CandleVigil,
  'astra-flux': AstraFlux,
  'neuro-vanta': NeuroVanta,
  'api-console': ApiConsole,
  'earth-synapse': EarthSynapse,
};

export default function HeroVisual({ className = '', compact = false }) {
  const Component = VARIANTS[VARIANT] || ControllerOrb;
  return (
    <div
      data-testid="hero-visual"
      className={`relative ${compact ? 'scale-75' : ''} ${className}`}
    >
      <Component />
    </div>
  );
}

